import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getCall, getCalls, updateCall, deleteCall } from "../../../../services/calls";
import { getOrganizationMembers } from "../../../../services/calendar";
import type {
  CallResponse,
  CallStatus,
  OrganizationMember,
} from "../../../../types/api";
import { getSafeErrorMessage } from "../../../../utils/errorHandler";
import { formatPhoneNumber, toTelHref } from "../../../../utils/formatters";
import { Dropdown } from "../../../../components/Dropdown";
import { TablePagination } from "../../../../components/TablePagination";
import { ConfirmModal } from "../../../../components/ConfirmModal";
import { CallListBuilderModal } from "./CallListBuilderModal";
import { CallExportModal } from "./CallExportModal";
import { RescheduleCallModal } from "./RescheduleCallModal";
import { CallDetailModal } from "./CallDetailModal";
import { formatCallDateTime, NO_TIME_LABEL } from "./callTime";
import { tourAnchor, type TourAnchorId } from "../../../tours/tourAnchors";
import { useTour } from "../../../tours/useTour";
import "./CallsListSection.css";

const PAGE_SIZE = 20;

/** The status filter includes an "all" option that maps to no status param. */
type StatusFilter = CallStatus | "all";

/** Tags an element as a tour anchor on the first row only; later rows get nothing. */
const rowAnchor = (index: number, id: TourAnchorId) => (index === 0 ? tourAnchor(id) : {});

const STATUS_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Upcoming", value: "upcoming" },
  { label: "Complete", value: "complete" },
];

export const CallsListSection = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [calls, setCalls] = useState<CallResponse[]>([]);
  const [coaches, setCoaches] = useState<OrganizationMember[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Per-row action in flight (call id) so we can disable that row's buttons.
  const [pendingActionId, setPendingActionId] = useState<string | null>(null);

  // Monotonic token guarding against out-of-order list responses: a slow fetch
  // that resolves after a newer filter change must not clobber fresher state.
  const fetchTokenRef = useRef(0);

  // Filters
  const [coachFilter, setCoachFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  // Modals
  const [isScheduleOpen, setIsScheduleOpen] = useState(false);
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [rescheduleTarget, setRescheduleTarget] = useState<CallResponse | null>(
    null
  );
  const [detailTarget, setDetailTarget] = useState<CallResponse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CallResponse | null>(null);

  // Load assignable coaches once for the filter dropdown.
  useEffect(() => {
    let cancelled = false;
    getOrganizationMembers()
      .then((members) => {
        if (!cancelled) setCoaches(members);
      })
      .catch((err) => {
        // Non-fatal: the list still works; the coach filter just stays empty.
        console.error("Failed to load organization members:", err);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchCalls = useCallback(async () => {
    const token = ++fetchTokenRef.current;
    setLoading(true);
    setError(null);
    try {
      const response = await getCalls({
        coachId: coachFilter || undefined,
        status: statusFilter === "all" ? undefined : statusFilter,
        pageNumber: currentPage,
        pageSize: PAGE_SIZE,
      });
      // Ignore a stale response superseded by a newer fetch.
      if (token !== fetchTokenRef.current) return;
      setCalls(response.items);
      setTotalCount(response.totalCount);
    } catch (err) {
      if (token !== fetchTokenRef.current) return;
      setError(getSafeErrorMessage(err, "Failed to load calls"));
      console.error("Failed to load calls:", err);
    } finally {
      if (token === fetchTokenRef.current) setLoading(false);
    }
  }, [coachFilter, statusFilter, currentPage]);

  useEffect(() => {
    fetchCalls();
  }, [fetchCalls]);

  const coachOptions = useMemo(
    () => [
      { label: "All coaches", value: "" },
      ...coaches.map((c) => ({ label: c.displayName, value: c.id })),
    ],
    [coaches]
  );

  const handleCoachFilterChange = (value: string) => {
    setCoachFilter(value || null);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value as StatusFilter);
    setCurrentPage(1);
  };

  const handleOpenPlayer = (prospectId: string) => {
    // Mirror PlayersSection deep-link: /players?prospectId=<id> opens the viewer.
    // Land on Notes — that's where a call's notes are recorded, so the coach keeps the
    // context they clicked from.
    const params = new URLSearchParams({ prospectId, section: "notes" });
    navigate(`/players?${params.toString()}`);
  };

  // Replace a single call in local state after a mutation.
  const replaceCall = (updated: CallResponse) => {
    setCalls((prev) => prev.map((c) => (c.id === updated.id ? updated : c)));
  };

  // Notes/coach/status saved from the detail modal. A save can move the call out of the
  // current view — completing it under an upcoming-only filter, or reassigning it away
  // from the filtered coach — and a row patched in place would then be one the filter
  // says should not be there. Refetch in those cases rather than patching.
  const handleDetailUpdated = (updated: CallResponse) => {
    const leftStatusFilter =
      statusFilter === "upcoming" && updated.status === "complete";
    const leftCoachFilter =
      coachFilter !== null && updated.assignedCoachId !== coachFilter;

    if (leftStatusFilter || leftCoachFilter) {
      fetchCalls();
    } else {
      replaceCall(updated);
    }
  };

  const handleMarkComplete = async (call: CallResponse) => {
    setPendingActionId(call.id);
    setError(null);
    try {
      const updated = await updateCall(call.id, { status: "complete" });
      // If we're filtering to upcoming-only, the completed call no longer
      // belongs in the view — refetch to keep the list accurate.
      if (statusFilter === "upcoming") {
        await fetchCalls();
      } else {
        replaceCall(updated);
      }
    } catch (err) {
      setError(getSafeErrorMessage(err, "Failed to mark call complete"));
      console.error("Failed to mark call complete:", err);
    } finally {
      setPendingActionId(null);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const call = deleteTarget;
    setPendingActionId(call.id);
    setError(null);
    try {
      await deleteCall(call.id);
      setDeleteTarget(null);
      // Refetch rather than splice the row out: deleting shifts every later row up a
      // position, so a page showing a full 20 would be left one short until the next load.
      await fetchCalls();
    } catch (err) {
      // Leave the dialog open so the coach can retry or back out; closing it on failure
      // would look like the delete went through.
      setError(getSafeErrorMessage(err, "Failed to delete call"));
      console.error("Failed to delete call:", err);
    } finally {
      setPendingActionId(null);
    }
  };

  const handleReschedule = async (scheduledAtIso: string) => {
    if (!rescheduleTarget) return;
    setPendingActionId(rescheduleTarget.id);
    setError(null);
    try {
      await updateCall(rescheduleTarget.id, {
        scheduledAt: scheduledAtIso,
      });
      // Refetch rather than patch in place: a reschedule changes scheduledAt,
      // which is the list's sort key, so the row's position (and page) may move.
      setRescheduleTarget(null);
      await fetchCalls();
    } catch (err) {
      setError(getSafeErrorMessage(err, "Failed to reschedule call"));
      console.error("Failed to reschedule call:", err);
    } finally {
      setPendingActionId(null);
    }
  };

  // The builder keeps itself open on a partial run so the coach can retry the rows that
  // failed, so this only closes it when nothing failed. Either way the list refreshes,
  // because calls that did land should be visible immediately.
  const handleBatchCreated = ({ failedCount }: { failedCount: number }) => {
    if (failedCount === 0) {
      setIsScheduleOpen(false);
    }
    setCurrentPage(1);
    fetchCalls();
  };

  // Deep link into the builder (?schedule=1). Kept on the old param name so existing
  // links and bookmarks still open the flow that replaced the schedule modal.
  useEffect(() => {
    if (searchParams.get("schedule") === "1") {
      setIsScheduleOpen(true);
      const next = new URLSearchParams(searchParams);
      next.delete("schedule");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Deep link to one call (?callId=...), which is where a click on the schedule's call chip
  // lands. Fetched by id rather than looked up in `calls`: the linked call is often on another
  // page of the list, or filtered out of it entirely, and the coach clicked it — it has to open
  // either way. The param is consumed immediately so a later close-then-refresh doesn't reopen it.
  //
  // Consuming the param re-renders with new searchParams, which re-runs this effect — so the
  // in-flight fetch must NOT be tied to an effect-scoped cancelled flag, or the effect would
  // cancel its own request before it ever resolved. The ref makes the fetch fire once per
  // linked id instead, and clears once the param is gone so the same call can be opened again.
  const handledCallIdRef = useRef<string | null>(null);
  useEffect(() => {
    const callId = searchParams.get("callId");
    if (!callId) {
      handledCallIdRef.current = null;
      return;
    }
    if (handledCallIdRef.current === callId) return;
    handledCallIdRef.current = callId;

    const next = new URLSearchParams(searchParams);
    next.delete("callId");
    setSearchParams(next, { replace: true });

    getCall(callId)
      .then(setDetailTarget)
      .catch((err) => {
        // The call was deleted, or belongs to another org. The list itself is fine, so say what
        // failed and leave them on it rather than showing an empty modal.
        setError(getSafeErrorMessage(err, "That call could not be opened"));
        console.error("Failed to open call from deep link:", err);
      });
  }, [searchParams, setSearchParams]);

  const showEmpty = !loading && !error && calls.length === 0;

  useTour("calls", { ready: !loading, hasData: calls.length > 0 });

  return (
    <div className="calls-list-page">
      <div className="calls-list-page__header">
        <div className="calls-list-page__header-left" {...tourAnchor("calls.title")}>
          <h1 className="calls-list-page__title">Call List</h1>
          {totalCount > 0 && (
            <span className="calls-list-page__count">
              {totalCount.toLocaleString()}
            </span>
          )}
        </div>
        <div className="calls-list-page__header-actions">
          {/* The compliance report. Sits beside Schedule rather than in the filter row
              because it produces a file rather than changing what the list shows. */}
          <button
            type="button"
            className="calls-list-page__export-button"
            data-testid="export-calls-button"
            {...tourAnchor("calls.export")}
            onClick={() => setIsExportOpen(true)}
          >
            Export
          </button>
          <button
            type="button"
            className="calls-list-page__schedule-button"
            data-testid="schedule-call-button"
            {...tourAnchor("calls.schedule")}
            onClick={() => setIsScheduleOpen(true)}
          >
            Schedule
          </button>
        </div>
      </div>

      <div className="calls-list-page__filters" {...tourAnchor("calls.filters")}>
        <div className="calls-list-page__filter">
          <Dropdown
            label="Coach"
            options={coachOptions}
            value={coachFilter ?? ""}
            onSelect={handleCoachFilterChange}
            placeholder="All coaches"
          />
        </div>
        <div className="calls-list-page__filter">
          <Dropdown
            label="Status"
            options={STATUS_OPTIONS}
            value={statusFilter}
            onSelect={handleStatusFilterChange}
          />
        </div>
      </div>

      {error && (
        <div className="calls-list-page__error" role="alert">
          {error}
        </div>
      )}

      <div
        className="calls-list-page__list"
        data-testid="calls-list"
        {...tourAnchor("calls.list")}
      >
        {loading && (
          <div className="calls-list-page__status">Loading calls...</div>
        )}

        {showEmpty && (
          <div className="calls-list-page__status">No calls scheduled</div>
        )}

        {!loading &&
          calls.map((call, index) => {
            const isPending = pendingActionId === call.id;
            const isComplete = call.status === "complete";
            const dialHref = toTelHref(call.prospectPhone);
            return (
              <div
                key={call.id}
                className="calls-list-page__row"
                data-testid={`call-row-${call.id}`}
              >
                <div className="calls-list-page__row-main">
                  <button
                    type="button"
                    className="calls-list-page__prospect calls-list-page__prospect-button"
                    data-testid={`open-call-${call.id}`}
                    {...rowAnchor(index, "calls.row.open")}
                    onClick={() => setDetailTarget(call)}
                  >
                    {call.prospectName}
                  </button>
                  <span className="calls-list-page__meta">
                    {call.assignedCoachName}
                  </span>
                  <span
                    className={`calls-list-page__meta${
                      call.scheduledAt ? "" : " calls-list-page__meta--no-time"
                    }`}
                  >
                    {call.scheduledAt
                      ? formatCallDateTime(call.scheduledAt)
                      : NO_TIME_LABEL}
                  </span>
                  {dialHref && (
                    <a
                      className="calls-list-page__phone"
                      href={dialHref}
                      data-testid={`dial-${call.id}`}
                      {...rowAnchor(index, "calls.row.dial")}
                      aria-label={`Call ${call.prospectName} at ${formatPhoneNumber(call.prospectPhone ?? "")}`}
                    >
                      {formatPhoneNumber(call.prospectPhone ?? "")}
                    </a>
                  )}
                  {call.purpose && (
                    /* Why the batch that created this call exists. Distinct from notes,
                       which record how the call went. */
                    <span
                      className="calls-list-page__purpose"
                      title={call.purpose}
                    >
                      {call.purpose}
                    </span>
                  )}
                  <span
                    className={`calls-list-page__status-pill calls-list-page__status-pill--${call.status}`}
                  >
                    {isComplete ? "Complete" : "Upcoming"}
                  </span>
                </div>
                <div className="calls-list-page__row-actions">
                  <button
                    type="button"
                    className="calls-list-page__action"
                    data-testid={`log-notes-${call.id}`}
                    {...rowAnchor(index, "calls.row.notes")}
                    onClick={() => setDetailTarget(call)}
                  >
                    {call.outcomeNotes ? "View notes" : "Log notes"}
                  </button>
                  {!isComplete && (
                    <button
                      type="button"
                      className="calls-list-page__action"
                      data-testid={`mark-complete-${call.id}`}
                      {...rowAnchor(index, "calls.row.complete")}
                      onClick={() => handleMarkComplete(call)}
                      disabled={isPending}
                    >
                      Mark complete
                    </button>
                  )}
                  <button
                    type="button"
                    className="calls-list-page__action"
                    data-testid={`reschedule-${call.id}`}
                    {...rowAnchor(index, "calls.row.reschedule")}
                    onClick={() => setRescheduleTarget(call)}
                    disabled={isPending}
                  >
                    Reschedule
                  </button>
                  <button
                    type="button"
                    className="calls-list-page__action calls-list-page__action--link"
                    data-testid={`open-player-${call.id}`}
                    {...rowAnchor(index, "calls.row.open-player")}
                    onClick={() => handleOpenPlayer(call.prospectId)}
                  >
                    Open player
                  </button>
                </div>
              </div>
            );
          })}
      </div>

      {!loading && totalCount > PAGE_SIZE && (
        <div className="calls-list-page__pagination">
          <TablePagination
            currentPage={currentPage}
            pageSize={PAGE_SIZE}
            totalCount={totalCount}
            onPageChange={setCurrentPage}
          />
        </div>
      )}

      {/* Mounted only while open: the builder loads teams, coaches, tag definitions, and
          grad years on mount, which must not happen on every render of this list. */}
      {isScheduleOpen && (
        <CallListBuilderModal
          onClose={() => setIsScheduleOpen(false)}
          onCreated={handleBatchCreated}
        />
      )}

      {/* Mounted only while open, like the builder above: the dialog resets to "this month
          to date" on mount, so it must not persist a previous session's picks. Reuses the
          coach list the filter dropdown already loaded rather than fetching again. */}
      {isExportOpen && (
        <CallExportModal coaches={coaches} onClose={() => setIsExportOpen(false)} />
      )}

      {/* Keyed so opening a different call remounts the modal with that call's notes. */}
      <CallDetailModal
        key={detailTarget?.id ?? "none"}
        call={detailTarget}
        // Reuses the list the coach filter already loaded rather than fetching per call.
        coaches={coaches}
        onClose={() => setDetailTarget(null)}
        onUpdated={handleDetailUpdated}
        onOpenPlayer={handleOpenPlayer}
        // Close the detail view before confirming: two stacked dialogs would leave the
        // coach confirming a delete over a modal that is about to describe a call that
        // no longer exists.
        onDelete={(call) => {
          setDetailTarget(null);
          setDeleteTarget(call);
        }}
      />

      {/* Deleting is not undoable, and a completed call carries notes mirrored onto the
          player's profile, so the copy says which of the two is about to happen. */}
      <ConfirmModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete this call?"
        message={
          deleteTarget?.status === "complete"
            ? `This removes the call with ${deleteTarget.prospectName} and the notes it left on their profile. It cannot be undone.`
            : `This removes the call with ${deleteTarget?.prospectName ?? ""}. It cannot be undone.`
        }
        confirmText="Delete call"
        busy={deleteTarget !== null && pendingActionId === deleteTarget.id}
      />

      <RescheduleCallModal
        isOpen={rescheduleTarget !== null}
        prospectName={rescheduleTarget?.prospectName ?? ""}
        currentScheduledAt={rescheduleTarget?.scheduledAt ?? null}
        saving={
          rescheduleTarget !== null && pendingActionId === rescheduleTarget.id
        }
        onClose={() => setRescheduleTarget(null)}
        onConfirm={handleReschedule}
      />
    </div>
  );
};
